import { useMemo, useState } from 'react';

import { Button } from '@travelnest/hui';

import { IconLabel, IconLabelList } from '@/components/ui/IconLabel';
import { Stack } from '@/components/ui/Stack';
import { Heading } from '@/components/ui/Text';
import { DialogDrawer } from '@/components/ui/dialog-drawer';
import { mapAmenities } from '@/lib/amenities';
import { useI18n } from '@/locales';

import { MoreAction } from './MoreAction';

const PREVIEW_COUNT = 8;

interface AmenitiesProps {
	amenities: string[];
}

/** The first eight amenities, with the full list in a modal. */
export const Amenities = ({ amenities }: AmenitiesProps) => {
	const t = useI18n();
	const [open, setOpen] = useState(false);
	const all = useMemo(() => mapAmenities(amenities), [amenities]);

	if (all.length === 0) return null;

	const preview = all.slice(0, PREVIEW_COUNT);

	return (
		<Stack gap="space30">
			<Heading as="h2" size="md">
				{t('property.amenities.title')}
			</Heading>
			<IconLabelList>
				{preview.map((amenity) => (
					<IconLabel
						key={amenity.key}
						icon={amenity.icon}
						label={amenity.label}
					/>
				))}
			</IconLabelList>
			{all.length > PREVIEW_COUNT && (
				<MoreAction>
					<Button
						text={t('property.amenities.showAll')}
						variant="secondary"
						fill="light"
						size="regular"
						onClick={() => setOpen(true)}
					/>
				</MoreAction>
			)}
			<DialogDrawer
				open={open}
				onOpenChange={setOpen}
				title={t('property.amenities.title')}
				closeLabel={t('common.close')}
			>
				<IconLabelList>
					{all.map((amenity) => (
						<IconLabel
							key={amenity.key}
							icon={amenity.icon}
							label={amenity.label}
						/>
					))}
				</IconLabelList>
			</DialogDrawer>
		</Stack>
	);
};
